import {useEffect, useState} from "react";
import { styled } from '@mui/material/styles';

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";

function ComplexStatisticsCardDashboardSwitches({switchMac, siteId, portStats}) {

  /**
   * Determina a cor do status com base no estado da porta
   * @param {object} item
   * @returns {string}
   */
  const getColor = (item) => {
    let colorStatus = 'inherit';

    if (!item || typeof item === 'undefined') {
      return colorStatus;
    }

    // Status da conexão (linkStatus 1 = conectado, 0 = desconectado)
    if (item.linkStatus === 1) {
      colorStatus = 'success';

      // Verifica a velocidade da porta (1 = 10M, 2 = 100M, 3 = 1000M)
      if (item.linkSpeed !== 3 || (item.poe === true && item.poePower	< 3) ) {
        colorStatus = 'warning';
      }

      // Se não há tráfego de dados
      if (item.tx === 0 && item.rx === 0) {
        colorStatus = 'error';
      }
    } else if (item.linkStatus === 0) {
      colorStatus = 'secondary'; // Porta desconectada
    }

    return colorStatus;
  }

  const getColorSwitch = (item) => {
    let colorStatus = 'inherit';

    if (!item || typeof item === 'undefined') {
      return colorStatus;
    }

    // Status da conexão (linkStatus 1 = conectado, 0 = desconectado)
    if (item.status > 0) {
      colorStatus = 'success';

      // Se não há tráfego de dados
      if (item.download === 0 && item.upload === 0) {
        colorStatus = 'error';
      }
    } else if (item.status === 0) {
      colorStatus = 'secondary'; // Porta desconectada
    }

    return colorStatus;
  }

  /**
   * Converte a cor de status em código hexadecimal
   * @param {object} item
   * @returns {string}
   */
  const getColorIcon = (item, switches = false) => {
    const colorIcon = switches?getColorSwitch(item):getColor(item);

    switch (colorIcon) {
      case 'success':
        return '#4caf4f';
      case 'warning':
        return '#ff9900';
      case 'error':
        return '#f44336';
      case 'secondary':
        return '#7b809a';
      case 'inherit':
        return '#1a2035';
      default:
        return '#1a2035';
    }
  }

  /**
   * Tooltip personalizado com largura máxima definida
   * @returns {JSX.Element}
   */
  const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 500,
    },
  });

  // Estado para controlar qual tooltip está aberto
  const [openToolTip, setOpenToolTip] = useState({});

  // Inicializa o estado de tooltips com base no número de portas
  useEffect(() => {
    if (portStats && portStats.ports && portStats.deviceMisc) {
      const tooltipState = {};
      const portNum = portStats.deviceMisc.portNum || 0;

      for (let i = 1; i <= portNum; i += 1) {
        tooltipState[`port${i}`] = false;
      }

      setOpenToolTip(tooltipState);
    }
  }, [portStats]);

  /**
   * Fecha todos os tooltips
   */
  const handleTooltipClose = () => {
    const tooltipState = {};

    if (portStats && portStats.deviceMisc) {
      const portNum = portStats.deviceMisc.portNum || 0;

      for (let i = 1; i <= portNum; i += 1) {
        tooltipState[`port${i}`] = false;
      }
    }

    setOpenToolTip(tooltipState);
  };

  /**
   * Abre o tooltip específico para a porta selecionada
   * @param {Event} event
   * @param {string} port
   */
  const handleTooltipOpen = (event, port) => {
    event.preventDefault();

    const tooltipState = {};
    Object.keys(openToolTip).forEach(key => {
      tooltipState[key] = key === port;
    });

    setOpenToolTip(tooltipState);
  };

  /**
   * Converte a velocidade do link em texto legível
   * @param {number} speed
   * @returns {string}
   */
  const getSpeedText = (speed) => {
    switch (speed) {
      case 1:
        return '10M';
      case 2:
        return '100M';
      case 3:
        return '1G';
      default:
        return 'N/A';
    }
  };

  /**
   * Converte o duplex em texto legível
   * @param {number} duplex
   * @returns {string}
   */
  const getDuplexText = (duplex) => {
    switch (duplex) {
      case 1:
        return 'Half';
      case 2:
        return 'Full';
      default:
        return 'Auto';
    }
  };

  /**
   * Formata bytes para exibição legível
   * @param {number} bytes
   * @returns {string}
   */
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
  };

  /**
   * Verifica se uma porta possui dispositivo conectado (downlink)
   * @param {number} portNumber
   * @returns {object|null}
   */
  const getConnectedDevice = (portNumber) => {
    if (!portStats || !portStats.downlinkList) return null;

    return portStats.downlinkList.find(item => item.port === portNumber) || null;
  };

  /**
   * Verifica se a porta é uplink
   * @param {number} portNumber
   * @returns {boolean}
   */
  const isUplinkPort = (portNumber) => {
    if (!portStats || !portStats.uplink) return false;
    return portStats.uplink.port === portNumber;
  };

  /**
   * Renderiza as portas do switch em linhas
   * @returns {JSX.Element[]}
   */
  const renderSwitchPorts = () => {
    if (!portStats || !portStats.ports || !portStats.deviceMisc) return null;

    const portNum = portStats.deviceMisc.portNum || 0;
    const rows = [];
    const portsPerRow = 8; // Número de portas por linha
    const rowCount = Math.ceil(portNum / portsPerRow);

    for (let i = 0; i < rowCount; i += 1) {
      const startPort = i * portsPerRow + 1;
      const endPort = Math.min(startPort + portsPerRow - 1, portNum);
      const portElements = [];

      for (let portNumber = startPort; portNumber <= endPort; portNumber += 1) {
        const portData = portStats.ports.find(p => p.portStatus && p.portStatus.port === portNumber);
        const portStatus = portData ? portData.portStatus : null;
        const connectedDevice = getConnectedDevice(portNumber);
        const isUplink = isUplinkPort(portNumber);

        // Determina o ícone com base no tipo de dispositivo conectado
        let iconName = portStatus.poe?'bolt':'settings_ethernet';
        if (connectedDevice) {
          if (connectedDevice.type === 'ap') iconName = 'wifi';
          else if (connectedDevice.type === 'switch') iconName = 'dns';
          else if (connectedDevice.type === 'gateway') iconName = 'router';
        } else if (isUplink) {
          iconName = 'trending_up';
        }

        portElements.push(
          <CustomWidthTooltip
            key={`port-${portNumber}`}
            title={
              <>
                <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                  Porta: <b>{portNumber}</b>
                </MDTypography>

                {isUplink && portStats.uplink && (
                  <>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      Uplink para: <b>{portStats.uplink.name || 'Desconhecido'}</b>
                    </MDTypography>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      MAC: <b>{portStats.uplink.mac}</b>
                    </MDTypography>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      Modelo: <b>{portStats.uplink.model}</b>
                    </MDTypography>
                  </>
                )}

                {connectedDevice && (
                  <>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      Dispositivo: <b>{connectedDevice.name || 'Desconhecido'}</b>
                    </MDTypography>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      MAC: <b>{connectedDevice.mac}</b>
                    </MDTypography>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      Modelo: <b>{connectedDevice.model}</b>
                    </MDTypography>
                  </>
                )}

                {portStatus && (
                  <>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      Status: <b>{portStatus.linkStatus === 1 ? 'Conectada' : 'Desconectada'}</b>
                    </MDTypography>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      Velocidade: <b>{getSpeedText(portStatus.linkSpeed)}</b>
                    </MDTypography>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      Duplex: <b>{getDuplexText(portStatus.duplex)}</b>
                    </MDTypography>
                    {portStatus.poe && (
                      <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                        PoE: <b>{portStatus.poePower} W</b>
                      </MDTypography>
                    )}
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      TX: <b>{formatBytes(portStatus.tx)}</b>
                    </MDTypography>
                    <MDTypography fontWeight="light" fontSize="medium" color="inherit">
                      RX: <b>{formatBytes(portStatus.rx)}</b>
                    </MDTypography>
                  </>
                )}
              </>
            }
            placeholder="bottom"
            style={{cursor: 'pointer'}}
            onClose={handleTooltipClose}
            open={openToolTip[`port${portNumber}`]}
          >
            <MDBox ml={1} onClick={(e) => handleTooltipOpen(e, `port${portNumber}`)}>
              <Icon fontSize="small" style={{color: `${getColorIcon(portStatus)}`}}>
                {iconName}
              </Icon>
              <MDTypography
                variant="button"
                color={getColor(portStatus)}
                fontWeight="light"
                ml={0.5}
                mr={1}
                position="relative"
                top={-5}
              >
                {portNumber}
              </MDTypography>
            </MDBox>
          </CustomWidthTooltip>
        );
      }

      rows.push(
        <MDBox key={`row-${i}`} display="flex" justifyContent="center" alignItems="center" mt={2}>
          {portElements}
        </MDBox>
      );
    }

    return rows;
  };

  // Informações gerais sobre o switch
  const renderSwitchInfo = () => {
    if (!portStats || !portStats.ipSetting) return null;

    return (
      <MDBox textAlign="center" mb={3}>
        <MDBox ml={1}>
          <Icon fontSize="small" style={{color: getColorIcon(portStats, true)}}>
            cable
          </Icon>
          <MDTypography variant="button" color={getColorSwitch(portStats)} fontWeight="light" ml={0.5} mr={1} position="relative" top={-5}>
            {portStats.name}
          </MDTypography>
        </MDBox>
        { /*
        <MDTypography variant="subtitle2" fontWeight="medium">
          IP: {portStats.ipSetting.configIp} | Máscara: {portStats.ipSetting.configMask} | Gateway: {portStats.ipSetting.configGate}
        </MDTypography>
        */ }
      </MDBox>
    );
  };

  return (
    <MDBox textAlign="center">
      {renderSwitchInfo()}
      {renderSwitchPorts()}
    </MDBox>
  );
}

// Setting default values for the props
ComplexStatisticsCardDashboardSwitches.defaultProps = {
  portStats: {},
  color: "info",
};

// Typechecking props
// Typechecking props
ComplexStatisticsCardDashboardSwitches.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  portStats: PropTypes.shape({
    name: PropTypes.string,
    deviceMisc: PropTypes.shape({
      portNum: PropTypes.number
    }),
    ipSetting: PropTypes.shape({
      configIp: PropTypes.string,
      configMask: PropTypes.string,
      configGate: PropTypes.string
    }),
    ports: PropTypes.arrayOf(
      PropTypes.shape({
        portStatus: PropTypes.shape({
          port: PropTypes.number,
          linkStatus: PropTypes.number,
          linkSpeed: PropTypes.number,
          duplex: PropTypes.number,
          poe: PropTypes.bool,
          poePower: PropTypes.number,
          tx: PropTypes.number,
          rx: PropTypes.number
        })
      })
    ),
    downlinkList: PropTypes.arrayOf(
      PropTypes.shape({
        port: PropTypes.number,
        name: PropTypes.string,
        mac: PropTypes.string,
        model: PropTypes.string,
        type: PropTypes.string
      })
    ),
    uplink: PropTypes.shape({
      port: PropTypes.number,
      name: PropTypes.string,
      mac: PropTypes.string,
      model: PropTypes.string
    })
  }),
  switchMac: PropTypes.string.isRequired,
  siteId: PropTypes.string.isRequired
};

export default ComplexStatisticsCardDashboardSwitches;